import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Space, Tooltip} from 'antd';
import {
  StyledInvoiceDetailsCard,
  StyledInvoiceDetailsIconButton,
  StyledInvoiceDetailsFileName,
  StyledInvoiceDetailsFormSpace,
} from './StyledInvoiceDetails';
import InvoiceAttachmentPreview from '../InvoiceAttachmentPreview';
import ImagePreviewTooltip from '../ImagePreviewTooltip';
import TransactionExpenseTypeForm from '../TransactionExpenseTypeForm/TransactionExpenseTypeForm';
import {CloseIcon, Download2Icon, EditLineIcon, EyeIcon, ReceiptDeclinedIcon} from '../../../../icons';
import {fileHelpers, objectHelpers, textHelpers} from '../../../../utils/helpers';
import invoicePlaceholderImg from '../../../../static/images/pages/transactions/invoice-placeholder.png';

const headerBtnProps = {size: 'small', type: 'text'};

const InvoiceDetails = ({
  attachment,
  expense,
  isCloseDisable,
  isDownloadDisable,
  isEditDisable,
  loadingAttachment = false,
  onClose,
  onEdit,
  onSubmit,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'invoices']);

  const hasFileAttached = useMemo(() => !!(attachment && attachment.file), [attachment]);

  const isDownloadEnabled = useMemo(() => hasFileAttached && !isDownloadDisable, [isDownloadDisable, hasFileAttached]);

  const isEditEnabled = useMemo(() => {
    const isDefinedExpenseType = objectHelpers.isValidValue(expense?.expense_type);
    return (!isEditDisable && isDefinedExpenseType && !hasFileAttached);
  }, [isEditDisable, hasFileAttached, expense])

  const handleOnEdit = (e) => onEdit && onEdit(e);

  const handleDownload = () => attachment.data && fileHelpers.saveFile(attachment.data);

  const handleClose = (e) => onClose && onClose(e);

  const headerButton = ({title, ...rest}) => (
    <Tooltip title={title}>
      <StyledInvoiceDetailsIconButton
        {...headerBtnProps}
        {...rest}
      />
    </Tooltip>
  );

  const invoiceAttachment = <InvoiceAttachmentPreview attachment={attachment} />;

  const imagePreview = ({children, trigger = 'click'} = {}) => (
    <ImagePreviewTooltip
      classNames={{root: 'image-preview-tooltip-overlay transaction-details'}}
      trigger={trigger}
      title={invoiceAttachment}
    >
      {children}
    </ImagePreviewTooltip>
  )

  const extra = (
    <Space align='center' size={2}>
      {hasFileAttached &&
        imagePreview({children: headerButton({icon: <EyeIcon />, title: t('preview')})})
      }
      {isDownloadEnabled &&
        headerButton({icon: <Download2Icon />, onClick: handleDownload, title: t('download')})
      }
      {isEditEnabled &&
        headerButton({icon: <EditLineIcon />, onClick: handleOnEdit, title: t('edit')})
      }
    </Space>
  );

  return (
    <StyledInvoiceDetailsCard
      title={t('invoice')}
      type='inner'
      extra={extra}
      {...rest}
    >
      {hasFileAttached ? (
        <StyledInvoiceDetailsFormSpace direction='horizontal' size='middle'>
          <Space size={6}>
            {
              imagePreview({
                children: (
                  <Space size={6}>
                    <img
                      alt='invoice placeholer'
                      className='placeholder'
                      src={invoicePlaceholderImg}
                    />
                    <StyledInvoiceDetailsFileName>
                      {textHelpers.cutString(attachment?.name, 32)}
                    </StyledInvoiceDetailsFileName>
                  </Space>
                ),
                trigger: 'hover'
              })
            }
            {!isCloseDisable && headerButton({icon: <CloseIcon />, onClick: handleClose, title: t('remove')})}
          </Space>
          <TransactionExpenseTypeForm
            expense={expense}
            onSubmit={onSubmit}
          />
        </StyledInvoiceDetailsFormSpace>
      ) : isEditDisable ? null : (expense && !loadingAttachment) && (
        <Space direction='vertical' size={0}>
          <ReceiptDeclinedIcon />
          <span>{t(`invoiceTypes.noReceipt`)}</span>
        </Space>
      )}
    </StyledInvoiceDetailsCard>
  );
}

InvoiceDetails.propTypes = {
  attachment: PropTypes.shape({
    file: PropTypes.string,
    name: PropTypes.string
  }),
  expense: PropTypes.object,
  isCloseDisable: PropTypes.bool,
  isDownloadDisable: PropTypes.bool,
  isEditDisable: PropTypes.bool,
  loadingAttachment: PropTypes.bool,
  onClose: PropTypes.func,
  onEdit: PropTypes.func,
  onSubmit: PropTypes.func,
}

export default InvoiceDetails;
